// frontend/src/components/OfficeTourReel/OfficeTourReel.tsx
import React, { useState, useRef } from "react";
import styles from "./OfficeTourReel.module.css";

const OfficeTourReel: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const videoId = "DnjrCEHktXg";

  const getYouTubeUrl = () => {
    const params = new URLSearchParams({
      autoplay: isPlaying ? "1" : "0",
      mute: isMuted ? "1" : "0",
      controls: "0",
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      enablejsapi: "0",
      iv_load_policy: "3",
      disablekb: "1",
      fs: "0",
      showinfo: "0",
    });

    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (iframeRef.current) {
      iframeRef.current.src = getYouTubeUrl();
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (iframeRef.current) {
      iframeRef.current.src = getYouTubeUrl();
    }
  };

  const handleFullScreen = () => {
    if (iframeRef.current?.requestFullscreen) {
      iframeRef.current.requestFullscreen();
    }
  };

  const handlePictureInPicture = async () => {
    if (document.pictureInPictureEnabled && iframeRef.current) {
      try {
        if (!document.pictureInPictureElement) {
          // @ts-ignore
          await iframeRef.current?.requestPictureInPicture();
        } else {
          await document.exitPictureInPicture();
        }
      } catch (error) {
        console.log("Picture-in-Picture failed:", error);
      }
    }
  };

  return (
    <section className={styles.officeTourSection}>
      <div className="container">
        <div className={styles.tourContainer}>
          {/* Content Section */}
          <div className={styles.contentSection}>
            <div className={styles.contentWrapper}>
              <span className={styles.badge}>Client Showcase</span>

              <h2 className={styles.title}>
                <span className={styles.gradientText}>
                  CRG Nirmala Enterprises.
                </span>
                <br />
                Office Transformation
              </h2>

              <p className={styles.description}>
                Witness how Scadel Furniture transformed CRG Nirmala's workspace
                with our premium commercial furniture solutions. Every piece is
                crafted for comfort, durability, and modern aesthetics.
              </p>

              <div className={styles.features}>
                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 12l2 2 4-4"
                        stroke="var(--accent-warm)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                        stroke="var(--accent-warm)"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <span>Ergonomic Office Chairs</span>
                </div>

                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        stroke="var(--accent-warm)"
                        strokeWidth="2"
                      />
                      <path
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        stroke="var(--accent-warm)"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <span>Modern Workstations</span>
                </div>

                <div className={styles.featureItem}>
                  <div className={styles.featureIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        stroke="var(--accent-warm)"
                        strokeWidth="2"
                        fill="var(--accent-warm)"
                      />
                    </svg>
                  </div>
                  <span>Premium Quality Materials</span>
                </div>
              </div>

              <div className={styles.testimonial}>
                <div className={styles.quoteIcon}>"</div>
                <p className={styles.quote}>
                  Scadel Furniture delivered exceptional quality and service.
                  Their furniture has significantly improved our workspace
                  comfort and productivity.
                </p>
                <div className={styles.clientInfo}>
                  <strong>CRG Nirmala Management</strong>
                  <span>Proud Scadel Client</span>
                </div>
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className={styles.videoSection}>
            <div
              className={styles.videoContainer}
              onMouseEnter={() => setShowControls(true)}
              onMouseLeave={() => setShowControls(false)}
            >
              <div className={styles.videoWrapper}>
                <iframe
                  ref={iframeRef}
                  src={getYouTubeUrl()}
                  title="CRG Nirmala Office Tour - SCADEL FURNITURE"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
                  allowFullScreen
                  className={styles.videoIframe}
                />

                {/* Custom Controls Overlay */}
                <div
                  className={`${styles.controlsOverlay} ${
                    showControls ? styles.show : ""
                  }`}
                >
                  <div className={styles.controlsContainer}>
                    <div className={styles.basicControls}>
                      <button
                        className={styles.controlBtn}
                        onClick={handlePlayPause}
                        aria-label={isPlaying ? "Pause" : "Play"}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          {isPlaying ? (
                            <path
                              d="M6 4h4v16H6zM14 4h4v16h-4z"
                              fill="currentColor"
                            />
                          ) : (
                            <path d="M8 5v14l11-7z" fill="currentColor" />
                          )}
                        </svg>
                      </button>

                      <button
                        className={styles.controlBtn}
                        onClick={handleMuteToggle}
                        aria-label={isMuted ? "Unmute" : "Mute"}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          {isMuted ? (
                            <path
                              d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                              fill="currentColor"
                            />
                          ) : (
                            <path
                              d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                              fill="currentColor"
                            />
                          )}
                        </svg>
                      </button>
                    </div>

                    <div className={styles.advancedControls}>
                      <button
                        className={styles.controlBtn}
                        onClick={handlePictureInPicture}
                        aria-label="Picture in Picture"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M19 7h-8v8h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 1.98 2 1.98h18c1.1 0 2-.88 2-1.98V5c0-1.1-.9-2-2-2zm0 16.01H3V4.98h18v14.03z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>

                      <button
                        className={styles.controlBtn}
                        onClick={handleFullScreen}
                        aria-label="Full Screen"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Video Badge */}
                <div className={styles.videoBadge}>
                  <div className={styles.badgeIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        fill="currentColor"
                      />
                      <path
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  Office Tour Reel
                </div>
              </div>

              <div className={styles.videoCaption}>
                <p>Experience the transformation with Scadel Furniture</p>
                <span>Click controls for PiP or Fullscreen</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfficeTourReel;
